import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, FormGroup, ValidatorFn } from '@angular/forms';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { OriginRestAPIService } from 'app/services/origin-rest-api.service';
import { ComponentsService } from 'app/components/services/components.service';
import { ModelsService } from 'app/models/services/models.service';
import { AssetsService } from 'app/assets/service/assets.service';
import { KeyCloakApiService } from 'app/services/key-cloak-api.service';
import { RouteNavLockService } from 'app/services/route-nav-lock.service';
import { RestAPIService } from 'app/services/rest-api.service';
import { DataService } from '../data/data.service';
import { AssetSummary } from 'app/assets/interfaces/interfaces';
import { GetMeasurmentInterface, GetUomsInterface, GetUomGroupInterface, RefConditionTypesInterface, RefAlarmSeverityInterface, IAlarmDisplaySettings } from 'app/services/graphqlApi.service';
import { getAlarmDisplaySettings, getAlarmSeverities, getConditionTypes, getMeasurments, getUomGroups, getUoms, getUomsByMeasurmentById } from '../constants/gql';
import { AlarmSeverity, ConditionType } from '../interfaces/alarms';
import { Group } from '../subscriptions/interfaces/interfaces';
import { IMetric, Metric } from 'app/datasources/interfaces/metric';
import { ITimezone } from 'app/utils/timezones';
import { IBreadCrumb } from '../interfaces/model';
import { IPreferences } from '../panels/chart/Models/chart-settings.model';
import { RestRequestType } from '../enums/rest-request-type.enum';
import { EEntityTypes } from '../enums';

// import { Apollo } from 'apollo-angular';
// import { ApolloQueryResult } from 'apollo-client';

export interface ISavedDetail {
  createdBy?: string;
  createdOn?: string;
  updatedBy?: string;
  updatedOn?: string;
}

export interface Measurement {
  id?: number;
  name: string;
  uomGroupId?: string;
  baseUom: string;
  baseUomId?: number;
  derivedUom: string;
  baseUomAbbreviation: string;
  description?: string;
  isSystem: boolean;
  action?: string;
}

export interface MeasurmentUom {
  id?: string;
  name: string;
  measurementId: number;
  baseUomId?: string;
  uomGroupId: string;
  uomGroupName?: string;
  factor: string;
  offset: string;
  symbol: string;
  isBaseUom: boolean;
  referenceFactor?: string;
  referenceOffset?: string;
  referenceUomId: string;
  measurementName?: string;
  action?: string;
}

export interface IBasic { id?: string; name?: string }

export interface IGetTagValue { dsNodeName: string; tags: ITagsList[] }

export interface ITagsList { dsNodeTagId: string; dsNodeTagName: string }

export interface ICount {
  id: string;
  count: number;
  ciId?: string;
}

export interface INodeOrDeviceStatus { name: string; status: boolean }

@Injectable({
  providedIn: 'root'
})

export class CommonService {
  readonly uoms = new BehaviorSubject<MeasurmentUom[]>([]);
  readonly uoms$ = this.uoms.asObservable();

  readonly allUoms = new BehaviorSubject<MeasurmentUom[]>([]);
  readonly allUoms$ = this.allUoms.asObservable();

  readonly measurements = new BehaviorSubject<Measurement[]>([]);
  readonly measurements$ = this.measurements.asObservable();

  readonly uomGroups = new BehaviorSubject<IBasic[]>([]);
  readonly uomGroups$ = this.uomGroups.asObservable();

  readonly conditionTypes = new BehaviorSubject<ConditionType[]>([]);
  readonly conditionTypes$ = this.conditionTypes.asObservable();

  readonly alarmSeverity = new BehaviorSubject<AlarmSeverity[]>([]);
  readonly alarmSeverity$ = this.alarmSeverity.asObservable();

  readonly assetSummaryList = new BehaviorSubject<AssetSummary[]>([]);
  readonly assetSummaryList$ = this.assetSummaryList.asObservable();

  readonly flatUserGroupList = new BehaviorSubject<Group[]>([]);
  readonly flatUserGroupList$ = this.flatUserGroupList.asObservable();

  readonly alarmDisplaySettings = new BehaviorSubject<IAlarmDisplaySettings[]>([]);
  readonly alarmDisplaySettings$ = this.alarmDisplaySettings.asObservable();

  // light/dark theme toggle
  readonly _isLightMode = new BehaviorSubject<boolean>(true);
  readonly isLightMode$ = this._isLightMode.asObservable();

  // set preferences (language and timezone)
  readonly _preferences = new BehaviorSubject<IPreferences>(null);

  constructor(
    // private readonly apollo: Apollo,
    private readonly router: Router,
    readonly dataService: DataService,
    readonly routeNavLockService: RouteNavLockService,
    private readonly apiService: RestAPIService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly componentService: ComponentsService,
    private readonly modelService: ModelsService,
    private readonly assetService: AssetsService,
    private readonly originRestAPIService: OriginRestAPIService,
    private readonly keyCloakApiService: KeyCloakApiService) { }

  /**
   * get ds node/device tags (metrics)
   * @param nodeName node name
   * @param deviceName device name
   * @param groupName group name
   */
  getDsMetrics(nodeName: string, deviceName: string, groupName: string) {
    let url: string = '';
    if (deviceName) {
      url = `get-dsnode-tags?groupId=${groupName}&nodeId=${nodeName}&deviceId=${deviceName}`;
    } else {
      url = `get-dsnode-tags?groupId=${groupName}&nodeId=${nodeName}`;
    }

    return this.apiService.get(url).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map(response => (response?.data?.tags || []).map((metric: IMetric) => this.setDsMetrics(metric))));
  }

  /**
   * set Metrics (node or device)
   * @param metric metric
   */
  setDsMetrics(metric: IMetric): Metric {
    return {
      timestamp: metric.t,
      value: metric.v,
      name: metric.name,
      id: metric.uuid,
      properties: metric.properties || []
    } as Metric;
  }

  /**
   * get nodes tags values
   * @param payload payload
   * @param nodeId node id
   */
  getDSNodeTagValues(payload: IGetTagValue, nodeId: string) {
    return this.originRestAPIService.dsApi.post(`datasource-nodes/tags`, RestRequestType.GET_TAG_VALUES, payload)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map(res => res.data));
  }

  /**
   * fetch groups
   */
  fetchGroups() {
    return this.keyCloakApiService.get('user-management/groups')
      .pipe(catchError(err => {
        this.toastr.error(err.error.error?.message);
        return EMPTY;
      }));
  }

  /**
   * set groups tree
   */
  setGroupsTree() {
    this.fetchGroups().pipe(take(1), map(groups => this.mapGroupList(groups)))
      .subscribe(flatUserGroups => {
        this.flatUserGroupList.next(flatUserGroups);
      });
  }

  /**
   * map group list
   * @param groups groups
   */
  private mapGroupList(groups: Group[]) {
    const flatGroups = [];
    for (let group of groups) {
      if (group.subGroups?.length > 0) {
        flatGroups.push(group);
        flatGroups.push(...this.mapGroupList(group.subGroups));
      } else {
        flatGroups.push(group);
      }
    }

    return flatGroups;
  }

  getUoms() {
    return this.uoms.getValue();
  }

  /**
   * get measurements
   */
  getMeasurments(): Observable<Measurement[]> {
    return this.apiService.getGQLData({ query: getMeasurments.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetMeasurmentInterface[] = response.data?.intellija_ref_measurements;
      return (queryResponse || []).map(record => {
        return {
          id: record.id,
          name: record.name,
          derivedUom: record.derived_measurement,
          baseUom: record.ref_uoms[0]?.name,
          baseUomId: record.ref_uoms[0]?.id,
          baseUomAbbreviation: record.ref_uoms[0]?.symbol,
          description: record.description,
          isSystem: record.is_system,
        };
      });
    }));
  }

  /**
   * set measurements
   */
  setMasurements() {
    if (this.measurements.getValue()?.length > 0) return;

    this.getMeasurments().pipe(take(1)).subscribe(m => {
      this.measurements.next(m);
    });
  }

  /**
   * set updated measurements
   * @param measurements measurements
   */
  setUpdatedMeasurements(measurements: Measurement[]) {
    this.measurements.next(measurements);
  }

  /**
   * get UOM by measuremet id
   * @param id measurement id
   */
  getUomByMeasurmentId(id: number): Observable<MeasurmentUom[]> {
    return this.apiService.getGQLData({ query: getUomsByMeasurmentById.query, params: { id: id } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetUomsInterface[] = response.data?.intellija_ref_uoms;
      return (queryResponse || []).map(record => this.getMappedUom(record))
    }));
  }

  /**
   * get UOM groups
   */
  getUomGroups(): Observable<IBasic[]> {
    return this.apiService.getGQLData({ query: getUomGroups.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetUomGroupInterface[] = response.data?.intellija_ref_uom_groups;
      return (queryResponse || []).map(record => {
        return {
          id: record.id,
          name: record.name
        };
      });
    }));
  }

  /**
   * set UOM groups
   */
  setUomGroups() {
    if (this.uomGroups.getValue()?.length > 0) return;

    this.getUomGroups().pipe(take(1)).subscribe(groups => {
      this.uomGroups.next(groups);
    });
  }

  /**
   * set UOM measurement by id
   * @param id measurement id
   */
  setUomMeasurementBy(id: number) {
    this.getUomByMeasurmentId(id).pipe(take(1)).subscribe(uoms => {
      this.uoms.next(uoms);
    });
  }

  /**
   * get all UOMs
   */
  getAllUom(): Observable<MeasurmentUom[]> {
    return this.apiService.getGQLData({ query: getUoms.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetUomsInterface[] = response.data?.intellija_ref_uoms;
      return (queryResponse || []).map(record => this.getMappedUom(record))
    }));
  }

  /**
   * set UOMs
   */
  setAllUom() {
    if (this.allUoms.getValue()?.length > 0) return;

    this.getAllUom().pipe(take(1)).subscribe(uoms => {
      this.allUoms.next(uoms);
    });
  }

  /**
   * get mapped UOM
   * @param uom uom
   */
  private getMappedUom(uom: GetUomsInterface) {
    return {
      id: uom.id,
      name: uom.name,
      measurementId: uom.measurement_id,
      baseUomId: uom.base_uom_id,
      uomGroupId: uom.uom_group_id,
      uomGroupName: uom.ref_uom_group?.name,
      factor: uom.factor,
      offset: uom.offset,
      symbol: uom.symbol,
      measurementName: uom.ref_measurement?.name,
      isBaseUom: uom.is_base_uom,
      referenceUomId: uom.reference_uom_id,
    };
  }

  /**
 * get condition types
 */
  getConditionTypes(): Observable<ConditionType[]> {
    return this.apiService.getGQLData({ query: getConditionTypes.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: RefConditionTypesInterface[] = response.data?.intellija_ref_condition_types;
      return (queryResponse || []).map(record => {
        return {
          displayName: record.display_name,
          name: record.name,
          isDeleted: record.is_deleted,
        };
      });
    }));
  }

  /**
   * set condition type
   */
  setConditionType() {
    if (this.conditionTypes.getValue()?.length > 0) return;

    this.getConditionTypes().pipe(take(1)).subscribe(types => {
      this.conditionTypes.next(types);
    });
  }

  /**
   * set alarm severity
   */
  setAlarmSeverity() {
    if (this.alarmSeverity.getValue()?.length > 0) return;
    this.getAlarmSeverity().pipe(take(1)).subscribe(severity => {
      this.alarmSeverity.next(severity);
    });
  }

  /**
   * get alarm severity
   */
  getAlarmSeverity(): Observable<AlarmSeverity[]> {
    return this.apiService.getGQLData({ query: getAlarmSeverities.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: RefAlarmSeverityInterface[] = response.data?.intellija_ref_alarm_severities;
      return (queryResponse || []).map(record => {
        return {
          displayName: record.display_name,
          name: record.name,
          isDeleted: record.is_deleted,
        };
      });
    }));
  }

  /**
   * set alarm desplay settings
   */
  setAlarmDisplaySettings() {
    if (this.alarmDisplaySettings.getValue()?.length > 0) return EMPTY;
    return this.apiService.getGQLData({ query: getAlarmDisplaySettings.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      return (response.data?.intellija_ref_alarm_display_settings || []) as IAlarmDisplaySettings[]
    }));
  }

  /**
 * for case insensitive sorting
 * @param valueA value A
 * @param valueB value B
 */
  static customComparator(valueA: string, valueB: string): number {
    if (valueA && valueB) {
      { return valueA.toLowerCase().localeCompare(valueB.toLowerCase()); }
    }
  };

  /**
   * auto complete field validation
   * @param validOptions valid options
   */
  autocompleteStringValidator(validOptions: Array<string>): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (validOptions.indexOf(control.value) !== -1) {
        return null; /* valid option selected */
      }
      return { invalidAutocompleteString: { value: control.value } };
    };
  }

  /**
   * count duplicates in array of objects
   * @param array array
   */
  countDuplicatesInArrayOfObjects(array: any[]): ICount[] {
    const counts: ICount[] = [];
    const count = {};
    array.forEach((x) => {
      const t = x.data.id;
      count[t] = (count[t] || 0) + 1;
      counts.push({ id: t, count: count[t] });
    });

    return counts;
  }

  /**
   * check for change in form
   * @param form form
   * @param dataObj data
   */
  checkForChange(form: FormGroup, dataObj: any): boolean {
    for (const key of Object.keys(dataObj)) {
      if (form.controls.hasOwnProperty(key) && dataObj[key] !== form.controls[key].value) {
        return true;
      }
    }

    return false;
  }

  /**
   * delete entity by id
   * @param entityDetail entityDetail (id, label)
   * @param entityType entityType (components/models/assets)
   */
  deleteEntity(entityDetail: IBreadCrumb, entityType: EEntityTypes): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (entityType !== EEntityTypes.ASSETS) {
        const sub = this.routeNavLockService.showRouteNavPrompt(`Warning, deleting ${(entityType === EEntityTypes.MODELS) ? 'model' : 'component'} '${entityDetail.label}' is irreversible, are you sure you want to proceed?`)
          .subscribe(res => {
            sub.unsubscribe();
            if (res) {
              if (entityType === EEntityTypes.COMPONENTS) {
                this.componentService.deleteComponent(entityDetail.id)
                  .then(data => {
                    resolve(true); // Resolve the Promise with true when deletion is successful
                  }).catch(error => {
                    reject(error); // Reject the Promise with an error if there's an error during deletion
                  });
              } else {
                this.modelService.deleteModel(entityDetail.id)
                  .then(data => {
                    resolve(true); // Resolve the Promise with true when deletion is successful
                  }).catch(error => {
                    reject(error); // Reject the Promise with an error if there's an error during deletion
                  });
              }
            } else {
              resolve(false); // Resolve the Promise with false if the user cancels the action
            }
          });
      } else {
        const defaultMessage: string = `Warning! All data associated with asset "${entityDetail.label}" will be permanently deleted, and can’t be recovered. To proceed with the delete, please enter your credentials.`;
        this.routeNavLockService.showRouteNavPromptWithCredentials(defaultMessage, EEntityTypes.ASSETS).pipe(take(1))
          .subscribe((res) => {
            if (res) {
              this.assetService.deleteAsset(entityDetail.id, res).pipe(take(1)).subscribe(res => {
                resolve(true);
              });
            } else {
              resolve(false); // Resolve the Promise with false if the user cancels the action
            }
          });
      }
    });
  }

  /**
   * header translation
   * @param translateKey translation key
   */
  headerTranslation(translateKey: string) { return () => this.translate.instant(translateKey) }

  /**
 * get language key word (string)
 * @param key string
 */
  translateWord(translateKey: string) {
    return this.translate.instant(translateKey);
  }

  /**
 * set Time As Per selected Timezone
 * @param date date
 * @param newTimezone new time zone
 * @returns
 */
  setTimeAsPerTimezone(date: Date, newTimezone: number): Date {
    const currentOffset: number = date.getTimezoneOffset();
    const convertedDate: Date = new Date(date.getTime() + (currentOffset * 60 * 1000)); // convert the date to the original timezone
    const newOffset: number = newTimezone * 60; // get the time diff. in minutes b/w the new timezone and UTC
    const newDate: Date = new Date(convertedDate.getTime() + (newOffset * 60 * 1000)); // convert the date to the new timezone
    return newDate;
}

  // apollo query sample
  // getDefaultPerspectiveByUserName(userName: String) {
  //   return this.apollo.watchQuery({ query: getDefaultPerspectiveByUserName, variables: { userName } })
  //     .valueChanges.pipe(map((response: ApolloQueryResult<IDefaultUsersPerspective>) =>
  //       (response.data?.intellija_perspectives_users || [])
  //     ));
  // }

}